import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      className="mb-10"
      initial={false}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <h2 className="text-xl font-bold mb-4" style={{ color: '#2c3e50' }}>{title}</h2>
      {children}
    </motion.div>
  );
};

const P = ({ children }) => (
  <p className="text-sm leading-relaxed mb-3" style={{ color: '#6c757d' }}>{children}</p>
);

const PrivacyPolicy = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Nexgeius - Toronto Software Development</title>
        <meta name="description" content="Read Nexgeius's privacy policy to understand how we collect, use, store, and protect your personal information when you use our website or services." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.nexgeius.com/privacy" />
      </Helmet>

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-32 pb-16 px-6 lg:px-8"
        style={{ backgroundColor: '#f7f9fc' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(44,62,80,0.07) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto" ref={heroRef}>
          <motion.div
            initial={false}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#6c757d' }}>Legal</p>
            <h1 className="text-4xl font-bold" style={{ color: '#2c3e50' }}>Privacy Policy</h1>
            <p className="mt-4 text-sm" style={{ color: '#6c757d' }}>
              Last updated: January 1, 2025 &nbsp;·&nbsp; Nexgeius, Toronto, Ontario, Canada
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6 lg:px-8" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-3xl mx-auto pt-14">

          <Section title="1. Introduction">
            <P>Nexgeius ("we," "us," or "our") operates the website nexgeius.com and provides custom software development, web development, and digital services. This Privacy Policy explains how we collect, use, disclose, and protect information when you visit our website or engage with our services.</P>
            <P>By using our website or services, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our website or services.</P>
          </Section>

          <Section title="2. Information We Collect">
            <P><strong style={{ color: '#2c3e50' }}>Information you provide directly:</strong> When you fill out our contact form or reach out by email or phone, we collect your name, email address, phone number, company name, and the details of your project inquiry.</P>
            <P><strong style={{ color: '#2c3e50' }}>Information collected automatically:</strong> When you visit our website, we may automatically collect certain information including your IP address, browser type, operating system, referring URLs, and pages visited. This is collected through standard server logs and analytics tools.</P>
            <P><strong style={{ color: '#2c3e50' }}>Cookies:</strong> Our website may use cookies and similar tracking technologies to improve your experience. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.</P>
          </Section>

          <Section title="3. How We Use Your Information">
            <P>We use the information we collect to:</P>
            <ul className="text-sm leading-relaxed mb-3 pl-5 space-y-2" style={{ color: '#6c757d', listStyleType: 'disc' }}>
              <li>Respond to your project inquiries and communicate with you about potential or ongoing engagements</li>
              <li>Deliver and improve our services</li>
              <li>Send you relevant updates or information you have requested</li>
              <li>Analyze website usage to improve performance and user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
            <P>We do not sell, trade, or rent your personal information to third parties.</P>
          </Section>

          <Section title="4. Third-Party Services">
            <P>We use the following third-party services that may collect or process data:</P>
            <ul className="text-sm leading-relaxed mb-3 pl-5 space-y-2" style={{ color: '#6c757d', listStyleType: 'disc' }}>
              <li><strong style={{ color: '#2c3e50' }}>Resend</strong>: delivers contact-form notification emails. Their privacy policy governs their data handling.</li>
              <li><strong style={{ color: '#2c3e50' }}>Google Analytics</strong> (if active): collects anonymized usage statistics to help us understand how visitors use our site.</li>
              <li><strong style={{ color: '#2c3e50' }}>Vercel / hosting providers</strong>: may log standard server access information.</li>
            </ul>
            <P>We encourage you to review the privacy policies of any third-party services we use.</P>
          </Section>

          <Section title="5. Data Security">
            <P>We take reasonable precautions to protect your personal information. However, no method of transmission over the internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</P>
            <P>We do not store sensitive payment information. Any financial transactions are handled directly through secure third-party processors.</P>
          </Section>

          <Section title="6. Data Retention">
            <P>We retain your contact information and project correspondence for as long as necessary to fulfill the purposes outlined in this policy or as required by applicable law. If you request that we delete your information, we will do so within 30 days unless we are required to retain it for legal reasons.</P>
          </Section>

          <Section title="7. Your Rights">
            <P>Depending on your jurisdiction, you may have the right to:</P>
            <ul className="text-sm leading-relaxed mb-3 pl-5 space-y-2" style={{ color: '#6c757d', listStyleType: 'disc' }}>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Withdraw consent to marketing communications at any time</li>
            </ul>
            <P>To exercise any of these rights, please contact us at nexgeius@gmail.com.</P>
          </Section>

          <Section title="8. Children's Privacy">
            <P>Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us and we will take steps to delete such information.</P>
          </Section>

          <Section title="9. Changes to This Policy">
            <P>We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last updated" date at the top of this page. We encourage you to review this policy periodically.</P>
          </Section>

          <Section title="10. Contact Us">
            <P>If you have any questions about this Privacy Policy or our data practices, please contact us:</P>
            <div
              className="rounded-xl p-5 mt-2"
              style={{ backgroundColor: '#f7f9fc', border: '1px solid rgba(44,62,80,0.08)' }}
            >
              <p className="text-sm font-semibold mb-1" style={{ color: '#2c3e50' }}>Nexgeius</p>
              <p className="text-sm" style={{ color: '#6c757d' }}>Toronto, Ontario, Canada</p>
              <p className="text-sm" style={{ color: '#6c757d' }}>nexgeius@gmail.com</p>
            </div>
          </Section>

          <div className="mt-10 pt-8" style={{ borderTop: '1px solid rgba(44,62,80,0.08)' }}>
            <Link to="/" className="text-sm font-semibold transition-colors hover:text-[#145374]" style={{ color: '#1c587a' }}>
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
